import React, {useEffect, useState} from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Card,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Switch,
    TextareaAutosize,
    TextField,
    Typography
} from '@mui/material';

import {Add as AddIcon, Delete as DeleteIcon, ExpandMore as ExpandMoreIcon} from '@mui/icons-material';
import {PromptTemplate, PromptTemplateCreate} from '../../types';
import { useTheme } from '@mui/material/styles';

interface PromptTemplateDialogProps {
    open: boolean;
    onClose: () => void;
    template?: PromptTemplate | null;
    onSave: (data: PromptTemplateCreate) => void;
}

const PromptTemplateDialog: React.FC<PromptTemplateDialogProps> = ({
                                                                       open,
                                                                       onClose,
                                                                       template,
                                                                       onSave
                                                                   }) => {
    const theme = useTheme();

    // Функция для извлечения переменных в фигурных скобках {var}
    const extractVariables = (text: string) => {
        const regex = /{([^}]+)}/g;
        const vars = new Set<string>();
        let match;
        while ((match = regex.exec(text)) !== null) {
            vars.add(match[1].trim());
        }
        return Array.from(vars);
    };

    const [formData, setFormData] = useState<PromptTemplateCreate>({
        name: '',
        category: '',
        task_type: '',
        template: '',
        description: '',
        variables: [],
        default_values: {},
        compatible_services: [],
        version: '1.0',
        is_active: true,
        priority: 0
    });

    const [newVariable, setNewVariable] = useState('');
    const [newService, setNewService] = useState('');

    useEffect(() => {
        if (template) {
            setFormData({
                name: template.name,
                category: template.category,
                task_type: template.task_type,
                template: template.template,
                description: template.description || '',
                variables: template.variables || [],
                default_values: template.default_values || {},
                compatible_services: template.compatible_services || [],
                version: template.version,
                is_active: Boolean(template.is_active),
                priority: template.priority
            });
        } else {
            setFormData({
                name: '',
                category: '',
                task_type: '',
                template: '',
                description: '',
                variables: [],
                default_values: {},
                compatible_services: [],
                version: '1.0',
                is_active: true,
                priority: 0
            });
        }
    }, [template, open]);

    const handleSubmit = () => {
        onSave(formData);
        onClose();
    };

    const addVariable = () => {
        if (newVariable.trim() && !formData.variables?.includes(newVariable.trim())) {
            setFormData(prev => ({
                ...prev,
                variables: [...(prev.variables || []), newVariable.trim()]
            }));
            setNewVariable('');
        }
    };

    const removeVariable = (variable: string) => {
        setFormData(prev => ({
            ...prev,
            variables: prev.variables?.filter(v => v !== variable) || []
        }));
    };

    const addService = () => {
        if (newService.trim() && !formData.compatible_services?.includes(newService.trim())) {
            setFormData(prev => ({
                ...prev,
                compatible_services: [...(prev.compatible_services || []), newService.trim()]
            }));
            setNewService('');
        }
    };

    const removeService = (service: string) => {
        setFormData(prev => ({
            ...prev,
            compatible_services: prev.compatible_services?.filter(s => s !== service) || []
        }));
    };

    // ИЗМЕНЕНИЕ 1: Логика определения редактирования теперь зависит от наличия ID
    const isEditing = !!template?.id;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {/* ИЗМЕНЕНИЕ 2: Динамический заголовок в зависимости от режима работы */}
                {isEditing
                    ? 'Edit Prompt Template'
                    : template
                        ? 'Copy Prompt Template'
                        : 'Create Prompt Template'}
            </DialogTitle>

            <DialogContent>
                <Box sx={{display: 'grid', gap: 3, mt: 1}}>
                    {/* ИЗМЕНЕНИЕ 3: Системная информация отображается только в режиме редактирования */}
                    {isEditing && template && (
                        <>
                            <Typography variant="h6" color="primary" gutterBottom>
                                System Information
                            </Typography>
                            <Card variant="outlined"
                                  sx={{p: 3, bgcolor: 'background.paper', border: '1px solid #e0e0e0'}}>
                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        md: 'repeat(3, 1fr)'
                                    },
                                    gap: 3
                                }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{
                                            fontWeight: 600,
                                            fontSize: '12px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Template ID
                                        </Typography>
                                        <Typography variant="body1" sx={{
                                            mt: 0.5,
                                            color: 'text.primary',
                                            fontWeight: 500,
                                            fontSize: '14px'
                                        }}>
                                            {template.id}
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{
                                            fontWeight: 600,
                                            fontSize: '12px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Usage Count
                                        </Typography>
                                        <Typography variant="body1" sx={{
                                            mt: 0.5,
                                            color: 'text.primary',
                                            fontWeight: 500,
                                            fontSize: '14px'
                                        }}>
                                            {template.usage_count || 0}
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{
                                            fontWeight: 600,
                                            fontSize: '12px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Created By
                                        </Typography>
                                        <Typography variant="body1" sx={{
                                            mt: 0.5,
                                            color: 'text.primary',
                                            fontWeight: 500,
                                            fontSize: '14px'
                                        }}>
                                            {template.created_by || 'System'}
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{
                                            fontWeight: 600,
                                            fontSize: '12px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Last Used
                                        </Typography>
                                        <Typography variant="body1" sx={{
                                            mt: 0.5,
                                            color: 'text.primary',
                                            fontWeight: 500,
                                            fontSize: '14px'
                                        }}>
                                            {template.last_used_at ? new Date(template.last_used_at).toLocaleDateString() : 'Never'}
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{
                                            fontWeight: 600,
                                            fontSize: '12px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Created At
                                        </Typography>
                                        <Typography variant="body1" sx={{
                                            mt: 0.5,
                                            color: 'text.primary',
                                            fontWeight: 500,
                                            fontSize: '14px'
                                        }}>
                                            {new Date(template.created_at).toLocaleString()}
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{
                                            fontWeight: 600,
                                            fontSize: '12px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Updated At
                                        </Typography>
                                        <Typography variant="body1" sx={{
                                            mt: 0.5,
                                            color: 'text.primary',
                                            fontWeight: 500,
                                            fontSize: '14px'
                                        }}>
                                            {new Date(template.updated_at).toLocaleString()}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Card>
                            <Divider sx={{my: 2}}/>
                        </>
                    )}

                    <Typography variant="h6" color="primary" gutterBottom>
                        Basic Information
                    </Typography>
                    <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
                        <TextField
                            label="Name"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
                            required
                            fullWidth
                        />

                        <TextField
                            label="Version"
                            value={formData.version}
                            onChange={(e) => setFormData(prev => ({...prev, version: e.target.value}))}
                            fullWidth
                        />
                    </Box>

                    <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr 100px', gap: 2}}>
                        <TextField
                            label="Category"
                            value={formData.category}
                            onChange={(e) => setFormData(prev => ({...prev, category: e.target.value}))}
                            required
                            fullWidth
                            placeholder="research, description, analysis"
                        />

                        <TextField
                            label="Task Type"
                            value={formData.task_type}
                            onChange={(e) => setFormData(prev => ({...prev, task_type: e.target.value}))}
                            required
                            fullWidth
                            placeholder="market_analysis, content_generation"
                        />

                        <TextField
                            label="Priority"
                            type="number"
                            value={formData.priority}
                            onChange={(e) => setFormData(prev => ({...prev, priority: parseInt(e.target.value) || 0}))}
                            fullWidth
                        />
                    </Box>

                    <TextField
                        label="Description"
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                        multiline
                        rows={2}
                        fullWidth
                    />

                    <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.is_active}
                                    onChange={(e) => {
                                        console.log('Switch changed:', e.target.checked);
                                        setFormData(prev => ({
                                            ...prev,
                                            is_active: e.target.checked
                                        }));
                                    }}
                                    color="primary"
                                />
                            }
                            label={`Template Status: ${formData.is_active ? 'Active' : 'Inactive'}`}
                        />
                        <Chip
                            label={formData.is_active ? 'ACTIVE' : 'INACTIVE'}
                            color={formData.is_active ? 'success' : 'error'}
                            size="small"
                        />
                    </Box>

                    <Divider/>

                    <Typography variant="h6" color="primary" gutterBottom>
                        Template Content
                    </Typography>
                    <Box sx={{ width: '100%', mt: 1, mb: 2 }}>
                        <TextareaAutosize
                            minRows={10}
                            style={{
                                width: '100%',
                                fontSize: 16,
                                padding: 12,
                                borderRadius: theme.shape.borderRadius,
                                border: '1px solid #404040',
                                backgroundColor: 'rgba(30, 30, 30, 0.6)',
                                backdropFilter: 'blur(10px)',
                                color: theme.palette.text.primary,
                                fontFamily: theme.typography.fontFamily,
                                resize: 'vertical',
                                boxSizing: 'border-box',
                                outline: 'none',
                            }}
                            placeholder="Use {variable_name} for variables..."
                            value={formData.template}
                            onChange={(e) => setFormData(prev => ({ ...prev, template: e.target.value }))}
                            onFocus={(e) => e.currentTarget.style.outline = `2px solid ${theme.palette.primary.main}`}
                            onBlur={(e) => e.currentTarget.style.outline = 'none'}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Use single curly braces for variables: {'{product_name}, {brand}, {description}'}
                        </Typography>

                        {/* Отображаем найденные переменные */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                            {extractVariables(formData.template).map(variable => (
                                <Chip
                                    key={variable}
                                    label={`{${variable}}`}
                                    color="warning"
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    </Box>

                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                            <Typography>Template Variables ({formData.variables?.length || 0})</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={{display: 'grid', gap: 2}}>
                                <TextField
                                    label="Add Variable"
                                    value={newVariable}
                                    onChange={(e) => setNewVariable(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && addVariable()}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={addVariable} disabled={!newVariable.trim()}>
                                                    <AddIcon/>
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    placeholder="product_name, brand, description..."
                                />

                                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                                    {formData.variables?.map((variable) => (
                                        <Chip
                                            key={variable}
                                            label={`{${variable}}`}
                                            onDelete={() => removeVariable(variable)}
                                            deleteIcon={<DeleteIcon/>}
                                            color="primary"
                                            variant="outlined"
                                        />
                                    ))}
                                </Box>
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                            <Typography>Default Values
                                ({Object.keys(formData.default_values || {}).length})</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={{display: 'grid', gap: 2}}>
                                <Typography variant="body2" color="text.secondary">
                                    Set default values for template variables
                                </Typography>

                                {formData.variables?.map((variable) => (
                                    <TextField
                                        key={variable}
                                        label={`Default value for {${variable}}`}
                                        value={formData.default_values?.[variable] || ''}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            default_values: {
                                                ...prev.default_values,
                                                [variable]: e.target.value
                                            }
                                        }))}
                                        fullWidth
                                        placeholder={`Enter default value for {${variable}}`}
                                    />
                                ))}

                                {(!formData.variables || formData.variables.length === 0) && (
                                    <Alert severity="info">
                                        Add variables first to set their default values
                                    </Alert>
                                )}
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                            <Typography>Compatible Services ({formData.compatible_services?.length || 0})</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={{display: 'grid', gap: 2}}>
                                <TextField
                                    label="Add Service"
                                    value={newService}
                                    onChange={(e) => setNewService(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && addService()}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={addService} disabled={!newService.trim()}>
                                                    <AddIcon/>
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    placeholder="perplexity_sonar, openai_gpt4, claude_3..."
                                />

                                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                                    {formData.compatible_services?.map((service) => (
                                        <Chip
                                            key={service}
                                            label={service}
                                            onDelete={() => removeService(service)}
                                            deleteIcon={<DeleteIcon/>}
                                            color="secondary"
                                            variant="outlined"
                                        />
                                    ))}
                                </Box>

                                <Alert severity="info" sx={{mt: 1}}>
                                    Leave empty for compatibility with all services
                                </Alert>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!formData.name || !formData.category || !formData.task_type || !formData.template}
                >
                    {isEditing ? 'Update' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PromptTemplateDialog;